'use client';

import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormContext } from 'react-hook-form';
import { faPlus } from '@icons/classic/regular';
import { getAriaLabel } from '@bridge/utils';
import { FormControl, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components';
import { FieldShell } from './FieldShell';
import { getFieldIds } from './utils';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { SelectOption } from '../../types';
import type { BaseFieldProps } from './types';
import type { FieldComponentWithOptions, FieldComponentWithPlaceholder } from './utils';
const CLEAR_VALUE = '__clear__';
export type SelectComponentProps = FieldComponentWithOptions<typeof Select, SelectOption> & FieldComponentWithPlaceholder<typeof Select> & {
  onAdd?: () => Promise<SelectOption>;
  addButtonText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: string, formContext: UseFormReturn<any>) => void;
  clearable?: boolean;
  clearLabel?: string;
};
interface SelectFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: SelectComponentProps;
}
export function SelectField<TFieldValues extends FieldValues>({
  field,
  fieldConfig,
  fieldState,
  componentProps,
  labelSize
}: SelectFieldProps<TFieldValues>) {
  const {
    options: optionsProp,
    placeholder,
    id,
    disabled: componentDisabled,
    onAdd,
    addButtonText,
    onChange,
    clearable,
    clearLabel,
    ...restComponentProps
  } = componentProps;
  const isDisabled = field.disabled || componentDisabled;
  const ids = getFieldIds(fieldConfig, fieldState, id);
  const formContext = useFormContext();
  const options = typeof optionsProp === 'function' ? optionsProp(formContext) : optionsProp;

  /** Sets the field value without marking the form as dirty (programmatic change, not user-initiated) */
  const setFieldValue = (value: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formContext.setValue(field.name as any, value, {
      shouldDirty: false
    });
  };

  // Handle option changes: clear disabled values and default to first option if current value is not available
  useEffect(() => {
    if (options && options.length > 0) {
      if (field.value) {
        const selectedOption = options.find(option => option.value === field.value);
        // If the selected option is disabled or doesn't exist in the new options
        if (!selectedOption || selectedOption.disabled) {
          // Find the first non-disabled option
          const firstAvailableOption = options.find(option_0 => !option_0.disabled);
          if (firstAvailableOption) {
            setFieldValue(firstAvailableOption.value);
          } else {
            setFieldValue('');
          }
        }
      } else {
        // Auto-select the first option if there's only one option and no current value.
        // Skip when clearable — the user may have intentionally cleared the value.
        if (options.length === 1 && !options[0].disabled && !clearable) {
          setFieldValue(options[0].value);
        }
      }
    }
  }, [options, field.value, field.name, formContext, clearable]);
  const handleValueChange = async (value_0: string) => {
    if (value_0 === CLEAR_VALUE) {
      field.onChange('');
      onChange?.('', formContext);
    } else if (value_0 === 'add' && onAdd) {
      try {
        const option_1 = await onAdd();
        field.onChange(option_1.value);
        onChange?.(option_1.value, formContext);
      } catch (error) {
        console.error('Error adding option:', error);
      }
    } else {
      field.onChange(value_0);
      onChange?.(value_0, formContext);
    }
  };
  return <FieldShell fieldConfig={fieldConfig} fieldState={fieldState} ids={ids} labelSize={labelSize} withFormControl={false}>
      <Select {...restComponentProps} aria-label={getAriaLabel(fieldConfig.label)} disabled={isDisabled} onValueChange={handleValueChange} value={field.value as string}>
        <FormControl>
          <SelectTrigger aria-describedby={ids.describedBy} aria-invalid={ids.isInvalid} fullWidth={componentProps.fullWidth} id={ids.fieldId} ref={field.ref}>
            <SelectValue placeholder={placeholder || 'Select an option'} />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          {clearable && <SelectItem className="text-content-secondary" value={CLEAR_VALUE}>
              {clearLabel || 'None'}
            </SelectItem>}
          {options?.map(option_2 => <SelectItem disabled={option_2.disabled} key={option_2.value} subtext={option_2.subtext} value={option_2.value}>
              {option_2.label}
            </SelectItem>)}
          {onAdd && <SelectItem value="add">
              <FontAwesomeIcon icon={faPlus} />
              {addButtonText}
            </SelectItem>}
        </SelectContent>
      </Select>
    </FieldShell>;
}